import { LIST_PATH, PEOPLE_SCREEN_PATH } from "../../../utils/constants/routes";

// Marketing List Tour (key: "marketing_list")
// Sub-tour 2 of "Prospects + Marketing List" group.
//
// 6 steps showing users how to group prospects into a Marketing List:
//   1. [center] Floating intro on /user/people?seniority=owner — no contacts selected
//   2. Spotlight the full table section with universal checkbox checked (all selected)
//   3. Spotlight the "Add to Marketing List" button in the bulk actions strip
//   4. Spotlight the Marketing sidebar nav item (people page prereqs maintained)
//   5. Open Marketing submenu, spotlight the "Marketing Lists" sub-tab
//   6. [center] Navigate to /user/lists, floating tooltip about managing lists
//
// Resume is fully supported — each step's before() sets up all required state
// independently so the tour can start from any step.
//
// Fast-path design: every setup function checks existing DOM state first and
// returns immediately if the required state is already in place. This keeps
// normal forward/backward transitions snappy (~250-400ms) while still doing the
// full work when resuming from a cold start.

export function getMarketingListTourSteps(navigate, store) {
  // Polls for a CSS selector until it appears in the DOM or the timeout elapses.
  const waitForSelector = (selector, timeout = 5000) =>
    new Promise((resolve) => {
      const deadline = Date.now() + timeout;
      const check = () => {
        const el = document.querySelector(selector);
        if (el) { resolve(el); return; }
        if (Date.now() >= deadline) { resolve(null); return; }
        setTimeout(check, 100);
      };
      check();
    });

  // Resolves true once the people-table has finished loading (skeleton rows gone).
  // The table renders 25 MuiSkeleton rows while an API call is in-flight; when
  // they disappear and the universal-checkbox header is present, data is ready.
  const waitForTableLoaded = (timeout = 6000) =>
    new Promise((resolve) => {
      const deadline = Date.now() + timeout;
      const check = () => {
        const hasSkeleton = document.querySelector(
          '[data-tour="people-table-section"] .MuiSkeleton-root',
        );
        const hasHeader = document.querySelector('[data-tour="universal-select-checkbox"]');
        if (hasHeader && !hasSkeleton) { resolve(true); return; }
        if (Date.now() >= deadline) { resolve(false); return; }
        setTimeout(check, 100);
      };
      check();
    });

  // Closes all open MUI modal backdrops (menus / submenus).
  const closeOpenMenus = async () => {
    document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
    await new Promise((resolve) => setTimeout(resolve, 250));
  };

  // Runs fn() while the seniority button is visually frozen at its resting state
  // and the MUI dropdown/backdrop is invisible. Prevents any flash while we
  // programmatically open the dropdown to check/apply the owner filter.
  const withHiddenDropdown = async (fn) => {
    const btn = document.querySelector('[data-tour="seniority-filter-btn"]');
    let btnFreeze = "";
    if (btn) {
      const cs = window.getComputedStyle(btn);
      const sel = "[data-tour='seniority-filter-btn']";
      const childSnapshots = [];
      btn.querySelectorAll("*").forEach((child, i) => {
        const childCs = window.getComputedStyle(child);
        if (!child.dataset.tourFreezeId) child.dataset.tourFreezeId = `tf${i}`;
        const id = child.dataset.tourFreezeId;
        childSnapshots.push(
          `${sel} [data-tour-freeze-id="${id}"]{` +
            `background-color:${childCs.backgroundColor}!important;color:${childCs.color}!important;` +
            `font-weight:${childCs.fontWeight}!important;font-size:${childCs.fontSize}!important;` +
            `transition:none!important}`,
        );
      });
      btnFreeze =
        `${sel},${sel}:hover,${sel}:focus,${sel}:focus-visible,` +
        `${sel}:active,${sel}[aria-expanded="true"]` +
        `{background-color:${cs.backgroundColor}!important;color:${cs.color}!important;` +
        `border-color:${cs.borderTopColor}!important;font-weight:${cs.fontWeight}!important;` +
        `font-size:${cs.fontSize}!important;outline:none!important;` +
        `box-shadow:none!important;transition:none!important}` +
        `${sel} .MuiTouchRipple-root{display:none!important}` +
        childSnapshots.join("");
    }
    const style = document.createElement("style");
    style.textContent =
      ".MuiPopover-root,.MuiBackdrop-root{opacity:0!important;transition:none!important}" +
      btnFreeze;
    document.head.appendChild(style);
    try {
      await fn();
    } finally {
      document.head.removeChild(style);
      if (btn) {
        btn.querySelectorAll("[data-tour-freeze-id]").forEach((el) => {
          delete el.dataset.tourFreezeId;
        });
      }
    }
  };

  // Ensures /user/people is open and the Owner seniority filter is active.
  //
  // URL fast path: if seniority=owner is already in the URL we are done — no
  // dropdown interaction needed at all. This keeps step 5→4 back-navigation at
  // ~0ms instead of ~350ms (open dropdown + close dropdown).
  // Full path (dropdown): only runs when we actually need to apply the filter,
  // and waits for the table skeleton to clear instead of a fixed delay.
  const ensureOwnerSelected = async (fullDelay = 2000) => {
    // Fast path — seniority=owner already active in URL
    if (
      window.location.pathname === PEOPLE_SCREEN_PATH &&
      new URLSearchParams(window.location.search).get("seniority") === "owner"
    ) {
      // Still strip stray params (URL + Redux) even on the fast path so they
      // cannot leak into subsequent filter navigations (e.g. tenant_path from
      // the mailbox OAuth step, tab from settings page initialisation).
      const fastParams = new URLSearchParams(window.location.search);
      const fastUrlDirty = fastParams.has("tab") || fastParams.has("tenant_path");
      const fastReduxPayload = store?.getState()?.allFilters?.payload;
      const fastReduxDirty = !!(fastReduxPayload?.tab) || !!(fastReduxPayload?.tenant_path);
      if (fastUrlDirty || fastReduxDirty) {
        if (fastUrlDirty) {
          fastParams.delete("tab");
          fastParams.delete("tenant_path");
          navigate(
            { pathname: PEOPLE_SCREEN_PATH, search: fastParams.toString() },
            { replace: true },
          );
        }
        if (fastReduxDirty) {
          const cleaned = { ...fastReduxPayload };
          delete cleaned.tab;
          delete cleaned.tenant_path;
          store.dispatch({ type: "ALL_FILTERS_SUCCESS", payload: cleaned });
        }
        await new Promise((resolve) => setTimeout(resolve, 150));
      }
      return;
    }

    // Clean Redux BEFORE any navigation or filter click. handleSeniorityChange
    // reads allFiltersSelector at click-time; if Redux is already clean the API
    // payload will never contain tab or tenant_path in the first place.
    const prePayload = store?.getState()?.allFilters?.payload;
    if (prePayload?.tab || prePayload?.tenant_path) {
      const preCleaned = { ...prePayload };
      delete preCleaned.tab;
      delete preCleaned.tenant_path;
      store.dispatch({ type: "ALL_FILTERS_SUCCESS", payload: preCleaned });
    }

    if (window.location.pathname !== PEOPLE_SCREEN_PATH) {
      navigate({ pathname: PEOPLE_SCREEN_PATH, search: "" });
      await new Promise((resolve) => setTimeout(resolve, 800));
    }

    const seniorityBtn = document.querySelector('[data-tour="seniority-filter-btn"]');
    if (!seniorityBtn) return;

    await withHiddenDropdown(async () => {
      seniorityBtn.click();
      seniorityBtn.blur();

      // Wait for the dropdown option to appear (fast, usually <200ms)
      const ownerCheckbox = await waitForSelector(
        '[data-tour="seniority-owner-option"] input[type="checkbox"]',
        1500,
      );

      if (ownerCheckbox && !ownerCheckbox.checked) {
        // Owner not selected yet — apply filter, then wait for the table skeleton
        // to clear rather than using a fixed delay (handles variable API latency).
        document.querySelector('[data-tour="seniority-owner-option"]').click();
        // Small pause so the table enters loading state before we check for it
        await new Promise((resolve) => setTimeout(resolve, 200));
        await waitForTableLoaded(fullDelay);
      }
      // If owner is already checked: close the dropdown immediately (no extra wait)

      document.querySelectorAll(".MuiBackdrop-root").forEach((el) => el.click());
      await new Promise((resolve) => setTimeout(resolve, 250));
    });

    // Strip stray params (tab, tenant_path) from both URL and Redux so they
    // cannot be re-injected by allFiltersSelector on subsequent filter clicks.
    const params = new URLSearchParams(window.location.search);
    const urlDirty = params.has("tab") || params.has("tenant_path");
    const reduxPayload = store?.getState()?.allFilters?.payload;
    const reduxDirty = !!(reduxPayload?.tab) || !!(reduxPayload?.tenant_path);
    if (urlDirty || reduxDirty) {
      if (urlDirty) {
        params.delete("tab");
        params.delete("tenant_path");
        navigate(
          { pathname: PEOPLE_SCREEN_PATH, search: params.toString() },
          { replace: true },
        );
      }
      if (reduxDirty) {
        const cleaned = { ...reduxPayload };
        delete cleaned.tab;
        delete cleaned.tenant_path;
        store.dispatch({ type: "ALL_FILTERS_SUCCESS", payload: cleaned });
      }
      await new Promise((resolve) => setTimeout(resolve, 150));
    }
  };

  // Checks the universal select-all checkbox so all contacts on the current page
  // are selected and the bulk-actions-strip is visible.
  //
  // Fast path: if the strip is already in the DOM the work is already done.
  // Otherwise waits for the table to finish loading before clicking — clicking
  // while skeletons are showing (0 real rows) would select nothing and the strip
  // would never appear.
  const ensureAllSelected = async () => {
    // Fast path — strip visible means contacts are already selected
    if (document.querySelector('[data-tour="bulk-actions-strip"]')) return;

    // Wait for real rows to land before selecting (handles cold-start from step 6)
    await waitForTableLoaded(6000);

    // Re-check: table load could have triggered a re-render that mounted the strip
    if (document.querySelector('[data-tour="bulk-actions-strip"]')) return;

    const cbInput = await waitForSelector(
      '[data-tour="universal-select-checkbox"] input[type="checkbox"]',
      3000,
    );
    if (!cbInput) return;

    cbInput.click();
    // Wait for React to process the selection and render the strip
    await waitForSelector('[data-tour="bulk-actions-strip"]', 3000);
    await new Promise((resolve) => setTimeout(resolve, 100));
  };

  // Unchecks the universal select-all checkbox so no contacts are selected and
  // the bulk-actions-strip is hidden.
  //
  // Fast path: if the strip is not in the DOM contacts are already deselected.
  // The checkbox wrapper now has data-tour="universal-select-checkbox" in BOTH
  // the normal header and the selected-mode header, so it is always findable.
  const ensureNoneSelected = async () => {
    // Fast path — strip not visible means nothing is selected
    if (!document.querySelector('[data-tour="bulk-actions-strip"]')) return;

    // Contacts are selected. Click the universal checkbox to deselect all.
    // (checkedAllHandler toggles: all-selected → deselect all; partial → select all.
    //  In the tour flow we always reach here after ensureAllSelected, so the state
    //  is guaranteed to be all-selected, making a single click the correct deselect.)
    const cbInput = await waitForSelector(
      '[data-tour="universal-select-checkbox"] input[type="checkbox"]',
      4000,
    );
    if (cbInput) {
      cbInput.click();
      await new Promise((resolve) => setTimeout(resolve, 400));
    }
  };

  return [
    // Step 1: Floating intro tooltip (no target).
    // Prereq: on /user/people with Owner filter active, no contacts selected.
    {
      target: "body",
      placement: "center",
      title: "Introducing Marketing Lists",
      content:
        "Assuming you know how to find the right contacts, a Marketing List is simply a way to group them together and target them in campaigns later.",
      blockTargetInteraction: true,
      hideBackButton: true,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000);
        await ensureNoneSelected();
      },
    },

    // Step 2: Full table section spotlight (universal checkbox checked, all selected).
    // The people-table-section wrapper covers the "Total X selected" strip, the action
    // bar, and the table rows — all visible once the universal checkbox is clicked.
    {
      target: '[data-tour="people-table-section"]',
      placement: "top",
      title: "Select the Contacts You Want to Target",
      content:
        "Let's say you want to reach Owners. We have selected the contacts for you as an example, the ones you would want to group together for outreach.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      tooltipMaxWidth: 560,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000);
        await ensureAllSelected();
      },
    },

    // Step 3: "Add to Marketing List" button only — not the whole action strip.
    // Placement "right" keeps the tooltip next to the button.
    {
      target: '[data-tour="add-to-marketing-list-btn"]',
      placement: "right",
      title: "Save Them as a Marketing List",
      content:
        "Once you have selected your contacts, hit this button to save them as a list. Use it in campaigns anytime without having to find or group them again and again.",
      blockTargetInteraction: true,
      spotlightPadding: 6,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000);
        await ensureAllSelected();
      },
    },

    // Step 4: Marketing sidebar nav item.
    // Prereqs maintained: /user/people + owner filter + contacts selected, so the
    // context feels natural when resuming directly at this step.
    // closeOpenMenus() also handles the case where the Marketing submenu is open
    // (e.g. coming back from step 5).
    {
      target: '[data-tour="marketing-nav"]',
      placement: "right",
      title: "Head to Your Marketing Hub",
      content:
        "This is where all your Marketing Lists live. Let's head over and take a look.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000);
        await ensureAllSelected();
      },
    },

    // Step 5: Marketing Lists sub-tab (Marketing submenu open).
    // before() ensures all step 1–4 prereqs are met (page, filter, selection) so
    // that resuming directly at step 5 works correctly, then opens the submenu.
    // Going back to step 4: step 4's before() calls closeOpenMenus which closes it.
    //
    // Race condition root cause (step 6 → back → step 5):
    //   MUI Menu uses Fade. When open transitions false→true, React synchronously
    //   renders and mounts the Portal Paper — but Fade's "exited" state applies
    //   visibility:hidden as an inline style. Fade's useEffect (which removes it
    //   and transitions to "entering") only runs AFTER the browser paints. Joyride's
    //   isElementVisible walks the ancestor chain checking visibility:hidden. If
    //   before() resolves in the gap between portal mount and the post-paint useEffect,
    //   Joyride fires target_not_found and (because action===PREV) decrements index→4.
    //
    // Fix: two rAFs bridge the gap.
    //   rAF-1 fires just before the paint that follows the portal mount.
    //   rAF-2 fires just before the NEXT paint — by which time Fade's useEffect
    //   has already run and visibility:hidden is gone.
    //   A short poll after the rAFs mirrors Joyride's exact check as a safety net
    //   for heavily loaded frames where the 2-rAF assumption doesn't hold.
    {
      target: '[data-tour="marketing-lists-sub-tab"]',
      placement: "right",
      title: "Find All Your Lists Right Here",
      content:
        "Every Marketing List you have created is stored right here. Access any list you need from this tab.",
      blockTargetInteraction: true,
      spotlightPadding: 4,
      spotlightRadius: 6,
      before: async () => {
        await closeOpenMenus();
        await ensureOwnerSelected(2000);
        await ensureAllSelected();

        const marketingNav = document.querySelector('[data-tour="marketing-nav"]');
        if (marketingNav) marketingNav.click();

        // rAF-1: after Portal mount paint. rAF-2: after Fade useEffect paint.
        await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));

        // Safety-net poll — mirrors Joyride's isElementVisible exactly.
        // Should resolve on the first check after the rAFs in the common case.
        await new Promise((resolve) => {
          const deadline = Date.now() + 2000;
          const check = () => {
            const el = document.querySelector('[data-tour="marketing-lists-sub-tab"]');
            if (el) {
              let ok = true;
              for (let p = el; p && p !== document.body; p = p.parentElement) {
                const cs = window.getComputedStyle(p);
                if (cs.display === "none" || cs.visibility === "hidden") {
                  ok = false;
                  break;
                }
              }
              if (ok) { resolve(); return; }
            }
            if (Date.now() >= deadline) { resolve(); return; }
            setTimeout(check, 50);
          };
          check();
        });

        // MUI Grow entering transition is ~225ms. Wait 300ms so the spotlight
        // only appears after the menu has fully scaled to its final size.
        await new Promise((r) => setTimeout(r, 300));
      },
    },

    // Step 6: Floating tooltip on /user/lists.
    // before() closes the submenu (if still open) and navigates to the lists page.
    {
      target: "body",
      placement: "center",
      title: "Manage All Your Marketing Lists",
      content:
        "View and manage all your Marketing Lists in one place. Open any list to see the contacts inside it, or rename and delete the lists you no longer need.",
      blockTargetInteraction: true,
      before: async () => {
        await closeOpenMenus();
        if (window.location.pathname !== LIST_PATH) {
          navigate(LIST_PATH);
          await new Promise((resolve) => setTimeout(resolve, 800));
        }
      },
    },
  ];
}
